import { useEffect } from "react";

/**
 * Native `<input type="date|time|datetime-local|month|week">` render their
 * placeholder ("dd/mm/yyyy") and selected value in the element's *own* locale —
 * which Chromium derives from the inherited `lang`. Under our Arabic UI
 * (`<html lang="ar">`) that placeholder becomes "يوم/شهر/سنة", which the bidi
 * algorithm then visually reverses to "موي/رهش/ةنس", and the calendar popup
 * shows Arabic month names. Per the app DATE POLICY (see lib/locale.ts) dates
 * are ALWAYS English + Latin + LTR, so we force every native date/time input to
 * `lang="en-GB"` + `dir="ltr"` regardless of UI language.
 *
 * Done globally (one MutationObserver mounted once at the app root) so it also
 * covers inputs that render later (modals, lazy routes) without touching ~50
 * call sites. CSS can't set `lang`, so this attribute pass is the only way.
 */
const DATE_INPUT_SELECTOR =
	'input[type="date"],input[type="datetime-local"],input[type="time"],input[type="month"],input[type="week"]';

function forceLatin(el: Element): void {
	if (el.getAttribute("lang") !== "en-GB") el.setAttribute("lang", "en-GB");
	if (el.getAttribute("dir") !== "ltr") el.setAttribute("dir", "ltr");
}

function normalize(
	root: ParentNode & { matches?: (s: string) => boolean },
): void {
	if (
		root instanceof Element &&
		typeof root.matches === "function" &&
		root.matches(DATE_INPUT_SELECTOR)
	) {
		forceLatin(root);
	}
	root.querySelectorAll?.(DATE_INPUT_SELECTOR).forEach(forceLatin);
}

/** Mount once at the app root. Forces all native date/time inputs to en-GB/LTR. */
export function useLatinDateInputs(): void {
	useEffect(() => {
		normalize(document.body);
		const obs = new MutationObserver((records) => {
			for (const r of records) {
				for (const node of r.addedNodes) {
					if (node.nodeType === Node.ELEMENT_NODE) normalize(node as Element);
				}
			}
		});
		obs.observe(document.body, { childList: true, subtree: true });
		return () => obs.disconnect();
	}, []);
}
